'use client';
import { useCallback, useEffect, useState } from 'react';
import useHuddle01 from './useHuddle01';
import type {
  CustomMediaKind,
  SupportedCodecs,
} from '@huddle01/web-core/types';
import type { Producer } from '@huddle01/web-core/types';

export const useLocalVideo = (props?: {
  // Callback to be called when the producer starts producing - i.e When inside the room and the producer is started
  onProduceStart?: (producer: Producer) => void;

  // Callback to be called when the producer stops producing - i.e When inside the room and the producer is stopped
  onProduceClose?: (reason?: {
    code: number;
    tag: string;
    message?: string;
  }) => void;

  // Callback to be called when the producer stops producing - i.e When inside the room and the producer is stopped
  onProducePaused?: () => void;
}) => {
  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  const [stream, setStream] = useState<MediaStream | null>(
    localPeer.getStream({ label: 'video' }),
  );

  const [isVideoOn, setIsVideoOn] = useState<boolean>(
    stream?.getVideoTracks()?.[0]?.enabled ?? false,
  );

  const [isProducing, setIdProducing] = useState<boolean>(false);

  const handleStreamFetched = useCallback(
    (data: {
      mediaKind: CustomMediaKind;
      stream: MediaStream;
    }) => {
      if (data.mediaKind === 'cam') {
        setStream(data.stream);
        const isEnabled = data.stream.getVideoTracks()?.[0]?.enabled ?? false;
        setIsVideoOn(isEnabled);
      }
    },
    [],
  );

  const handleStreamClosed = useCallback(
    (data: {
      label: string;
      mediakind?: CustomMediaKind;
      reason?: { code: number; tag: string; message?: string };
    }) => {
      if (data.label === 'video') {
        setStream(null);
        setIsVideoOn(false);
        setIdProducing(false);

        if (props?.onProduceClose) props?.onProduceClose(data.reason);
      }
    },
    [props?.onProduceClose],
  );

  const handleStreamPlayable = useCallback(
    (data: {
      label: string;
      producer: Producer;
    }) => {
      if (data.label === 'video') {
        setStream(localPeer.getStream({ label: 'video' }));
        setIsVideoOn(true);
        setIdProducing(true);
        if (props?.onProduceStart) props?.onProduceStart(data.producer);
      }
    },
    [props?.onProduceStart],
  );

  useEffect(() => {
    localPeer.on('stream-fetched', handleStreamFetched);

    localPeer.on('stream-playable', handleStreamPlayable);

    localPeer.on('stream-closed', handleStreamClosed);

    return () => {
      localPeer.off('stream-fetched', handleStreamFetched);

      localPeer.off('stream-playable', handleStreamPlayable);

      localPeer.off('stream-closed', handleStreamClosed);
    };
  }, []);

  /**
   * Enables the local web cam and starts producing the stream with the label `video`
   * @param customVideoStream (Optional) Custom Video Stream to produce, if not provided it will fetch the stream from the device
   * @param prefferedCodec (Optional) Preferred Codec to be used for the stream ( SupportedCodecs ), make sure the codec is supported by the device ( 'Browser' or 'ReactNative' )
   *
   * @summary This functions handle the producing of media streams to all the remote peers in the room.
   * it enables the local web cam fetches the stream opens you web cam indicator light on the device
   * upon successfull fetching of the stream it produces the stream with the label `video` and `stopTrackOnClose: true` to all the remote peers in the room.
   * when closing using disableVideo it will stop the local track and close the producer which will notify all the RemotePeers that this producer has stopped producing
   * and they should stop consuming it.
   *
   * `NOTE: You can only produce to a room when you have joined the room, if you try to produce before joining the room it will throw an error`
   *
   * @throws { Error } If the stream is not found
   * @throws { Error } If Preffered Codec is not supported by the Device ( 'Browser' or 'ReactNative' )
   * @throws { Error } If Failed to produce, Reason will be in the error message
   */
  const enableVideo = useCallback(
    (data?: {
      customVideoStream?: MediaStream;
      prefferedCodec?: SupportedCodecs;
    }) => localPeer.enableVideo(data),
    [localPeer],
  );
  /**
   * Disables the video stream, pauses the underlying producer and stops producing the video stream - useful when user has joined the room and wants to disable the video stream
   */
  const disableVideo = useCallback(() => localPeer.disableVideo(), [localPeer]);

  const track = stream?.getVideoTracks()[0] || null;
  /**
   * Replaces the current video stream with the new stream if you have produced a stream with label video or used the default function enableVideo and you want to replace it with a new stream
   */
  const replaceVideoStream = useCallback(
    (stream: MediaStream) => localPeer.replaceVideoStream(stream),
    [localPeer],
  );
  /**
   * Changes the Video source to the given deviceId, sets the preferred cam device as the given deviceId
   */
  const changeVideoSource = useCallback(
    (deviceId: string) => localPeer.changeVideoSource(deviceId),
    [localPeer],
  );

  return {
    stream,
    track,
    isVideoOn,
    isProducing,
    enableVideo,
    disableVideo,
    replaceVideoStream,
    changeVideoSource,
  };
};
